import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Lock, Mail, ArrowRight, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import AuthHeader from "@/components/AuthHeader";

function redirectFor(role) {
    return role === "admin" ? "/admin" : "/conta";
}

export default function Entrar() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname;

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user) navigate(from || redirectFor(user.role), { replace: true });
    }, [user, from, navigate]);

    async function onSubmit(e) {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await login(email.trim(), password);
            toast.success(`Bem-vindo, ${u.name || u.email}!`);
            navigate(from || redirectFor(u.role), { replace: true });
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Credenciais inválidas");
        } finally {
            setLoading(false);
        }
    }

    return (
        <section className="relative min-h-[100vh] bg-muted flex items-center justify-center px-6 pt-28 pb-20" data-testid="entrar-page">
            <AuthHeader />
            <div className="w-full max-w-md">
                <div className="rounded-2xl border border-border bg-white p-6 sm:p-8 shadow-lg">
                    <span className="eyebrow">Área de cliente</span>
                    <h1 className="mt-3 font-heading text-3xl font-extrabold text-primary leading-tight">Iniciar sessão</h1>
                    <p className="mt-2 text-sm text-muted-foreground">Aceda aos seus pedidos, equipamentos e pedidos de assistência.</p>

                    <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="entrar-form">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Email</label>
                            <div className="mt-2 relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <input
                                    type="email" required autoFocus
                                    value={email} onChange={(e) => setEmail(e.target.value)}
                                    data-testid="entrar-email"
                                    className="w-full rounded-md border border-border bg-muted pl-10 pr-3 py-3 text-sm text-primary outline-none focus:border-primary focus:bg-white"
                                    placeholder="seu@email.com"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Password</label>
                            <div className="mt-2 relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <input
                                    type="password" required
                                    value={password} onChange={(e) => setPassword(e.target.value)}
                                    data-testid="entrar-password"
                                    className="w-full rounded-md border border-border bg-muted pl-10 pr-3 py-3 text-sm text-primary outline-none focus:border-primary focus:bg-white"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>
                        <button
                            type="submit" disabled={loading}
                            data-testid="entrar-submit"
                            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground shadow-sm transition hover:-translate-y-0.5 disabled:opacity-60"
                        >
                            {loading ? "A entrar..." : "Entrar"} <ArrowRight className="h-4 w-4" />
                        </button>
                    </form>
                </div>
                <div className="mt-6 text-center">
                    <p className="text-sm text-muted-foreground">Ainda não tem conta?</p>
                    <Link to="/registar" data-testid="entrar-goto-registar"
                        className="mt-2 inline-flex items-center gap-2 text-sm font-bold text-primary hover:text-primary/70">
                        <UserPlus className="h-4 w-4" /> Criar conta
                    </Link>
                </div>
            </div>
        </section>
    );
}
